import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PetDetail, PetListFilters, PetListResponse } from './types';

async function fetch_pet_list(filters: PetListFilters): Promise<PetListResponse> {
  const params: Record<string, string | number> = {};
  if (filters.page) params.page = filters.page;
  if (filters.page_size) params.page_size = filters.page_size;
  if (filters.species) params.species = filters.species;
  if (filters.q) params.q = filters.q;
  if (filters.owner_id) params.owner_id = filters.owner_id;
  if (filters.is_active) params.is_active = filters.is_active;
  // Same omission as the users list had: declared, set by the UI, never sent.
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  const res = await api.get<PetListResponse>('/admin/pets', { params });
  return res.data;
}

async function fetch_pet_detail(id: string): Promise<PetDetail> {
  const res = await api.get<PetDetail>(`/admin/pets/${id}`);
  return res.data;
}

export function use_pet_list(filters: PetListFilters) {
  return useQuery({
    queryKey: ['admin-pet-list', filters],
    queryFn: () => fetch_pet_list(filters),
    placeholderData: (prev) => prev,
  });
}

export function use_pet_detail(id: string | undefined) {
  return useQuery({
    queryKey: id ? ['admin-pet-detail', id] : ['admin-pet-detail', 'noop'],
    queryFn: () => fetch_pet_detail(id as string),
    enabled: typeof id === 'string' && id.length > 0,
  });
}

/**
 * Take a pet profile down, or put it back.
 *
 * `is_active = false` is the same flag the owner sets by deleting a pet, so the
 * customer sees the same thing either way; what differs is the lifecycle event
 * the backend writes, which records that an admin did it and why.
 */
async function set_pet_active(id: string, is_active: boolean, reason: string) {
  const res = await api.post<{
    id: string;
    is_active: boolean;
    already_in_state: boolean;
  }>(`/admin/pets/${id}/${is_active ? 'restore' : 'remove'}`, { reason });
  return res.data;
}

export function use_set_pet_active() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      is_active,
      reason,
    }: {
      id: string;
      is_active: boolean;
      reason: string;
    }) => set_pet_active(id, is_active, reason),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['admin-pet-detail', id] });
      void qc.invalidateQueries({ queryKey: ['admin-pet-list'] });
      // The owner's page shows these pets too, and its counts move with them.
      void qc.invalidateQueries({ queryKey: ['admin-user-detail'] });
    },
  });
}
